/**
 * Channel detail → the channel's daily budget limit (the "Spent $X / $Y" denominator),
 * from the acquisition-budget API.
 *
 * The acquisition-budget GET carries a `budget_details` bag the backend keys by the channel
 * `aft` literals for per-channel splits, confirmed live as a bare daily-limit number per
 * channel (whole currency) alongside sibling `<aft>_managed` flags, e.g.
 * `{ web: 20, web_managed: true, app: 0, app_managed: true, ... }`. This reads the channel's
 * limit straight off `budget_details[aft]`, falling back to the brand's whole daily
 * acquisition cap (`daily_acquisition_budget`) only if that key is missing. The "Spent" half
 * of the card is the real channel ad-spend, sourced separately in {@link useChannelCampaigns}.
 *
 * A brand that has NOT configured a budget returns only the two wallet objects (no
 * `acquisition_budget` record), so `limit` is undefined; the card then shows the real spend
 * without a denominator (see the page). The scale matches the ad-spend cost (whole currency),
 * the same as the rest of the acquisition budget figures.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchAcquisitionBudget, selectAcquisitionBudget } from '@/store/slices/acquisitionBudget';

import type { ChannelId } from '../../mock-data/types';
import { CHANNEL_AFT } from './channel-performance';

const finiteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** The channel's daily limit from `budget_details` — the bare number keyed by its `aft`
 *  (`budget_details.web` = the web daily limit), or undefined when absent. */
const channelLimitFromDetails = (details: unknown, aft: string): number | undefined => {
  if (!details || typeof details !== 'object') return undefined;
  return finiteNumber((details as Record<string, unknown>)[aft]);
};

export type ChannelBudgetResult = {
  /** The channel's daily budget limit (whole currency), or `undefined` when unavailable. */
  limit: number | undefined;
  /** True while the budget GET is in flight with nothing cached for the brand. */
  loading: boolean;
};

/**
 * @param brandId Active brand id (`displayBrand?.id`); `undefined` keeps it loading.
 * @param channelId The channel page id, mapped to the `aft` key in `budget_details`.
 */
export const useChannelBudget = (
  brandId: string | undefined,
  channelId: ChannelId | undefined,
): ChannelBudgetResult => {
  const dispatch = useAppDispatch();
  const acqBudget = useAppSelector((state) => selectAcquisitionBudget(state, brandId));

  // Track when the GET has settled (resolved OR errored), so a budget the backend 500s on
  // stops the skeleton instead of spinning forever (the GET 500s with no acquisition wallet).
  const [attempted, setAttempted] = React.useState(false);
  React.useEffect(() => {
    setAttempted(false);
    if (!brandId) return;
    let alive = true;
    void dispatch(fetchAcquisitionBudget({ casinoInstanceId: brandId })).finally(() => {
      if (alive) setAttempted(true);
    });
    return () => {
      alive = false;
    };
  }, [dispatch, brandId]);

  const ready = finiteNumber(acqBudget?.acquisition_budget) !== undefined;
  const aft = channelId ? CHANNEL_AFT[channelId] : undefined;

  const limit = React.useMemo<number | undefined>(() => {
    if (!ready || !acqBudget || !aft) return undefined;
    return channelLimitFromDetails(acqBudget.budget_details, aft) ?? finiteNumber(acqBudget.daily_acquisition_budget);
  }, [acqBudget, ready, aft]);

  return { limit, loading: Boolean(brandId) && !ready && !attempted };
};

export default useChannelBudget;
